import React, { FC } from 'react';
import { Button, Typography, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import theme from '~/theme';
import { CheckOutlined } from '@ant-design/icons'; // Import Ant Design's checkmark icon
import { SendMoneyBeneficiary } from '~/api/sendMoney';

const { Panel } = Collapse;

const StyledCollapse = styled(Collapse)`
  border: none;
  background-color: ${theme.layoutBodyBackground};
  .ant-collapse-header {
    border-top: 1px solid #e6e6e6;
    border-right: 1px solid #e6e6e6;
    border-left: 1px solid #e6e6e6;
    background-color: ${theme.layoutHeaderBackground};
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
    border-right: 1px solid #e6e6e6;
    border-left: 1px solid #e6e6e6;
  }
  .ant-collapse-item {
    margin-bottom: 24px;
  }
`;

const AccountRow = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: ${(props) => (props.selected ? '#E8DFFD80' : 'transparent')};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.selected ? '#E8DFFD80' : '#f5f5f5')};
  }

  .currency {
    color: #a1a1a1;
  }
`;

const Checkmark = styled(CheckOutlined)`
  color: ${theme.primaryColor};
  font-size: 16px;
  margin-left: auto;
`;

const CenteredText = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 40px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: 16px;
`;

const FullWidthButton = styled(Button)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const PrimaryButton = styled(Button)`
  width: 100%;
`;

interface SelectAccountsProps {
  mainState: any;
  setMainState: React.Dispatch<React.SetStateAction<any>>;
  onBack: () => void;
  onNext: (selectedAccountId: string) => void;
  beneficiaries: SendMoneyBeneficiary[];
}

const SelectAccounts: FC<SelectAccountsProps> = ({
  mainState,
  setMainState,
  onBack,
  onNext,
  beneficiaries,
}) => {
  const { recipientAccountId } = mainState;
  const { t } = useTranslation('sendMoney');
  const handleAccountSelect = (
    accountId: string,
    accountNumber: string,
    currency: string,
    index: number
  ) => {
    setMainState((prevState: any) => ({
      ...prevState,
      recipientAccountId: accountId,
      recipientAccountNumber: accountNumber,
      recipientAccountCurrency: currency,
      selectedBeneficiary: index,
    }));
  };
  const hasAccounts = beneficiaries?.some((b) => b.recipient && b.recipient.length > 0);
  return (
    <div style={{ width: '100%' }}>
      <CenteredText>
        <Typography.Text>
          {hasAccounts ? t('accountsFound') : t('accountsNotFound')}
        </Typography.Text>
      </CenteredText>
      <ColumnContainer>
        {beneficiaries?.map((beneficiary, index) =>
          !beneficiary?.recipient || beneficiary?.recipient.length === 0 ? null : (
            <StyledCollapse
              key={index}
              accordion={false}
              expandIconPosition="end"
              defaultActiveKey={[`${index}`]}
            >
              <Panel
                header={
                  <Typography.Text style={{ fontWeight: 600 }}>
                    {beneficiary?.beneficiary?.name}
                  </Typography.Text>
                }
                key={`${index}`}
              >
                <div style={{ width: '100%' }}>
                  {beneficiary?.recipient?.map((account) => (
                    <AccountRow
                      key={account.accountId}
                      selected={recipientAccountId === account.accountId}
                      onClick={() =>
                        handleAccountSelect(
                          account.accountId,
                          account.accountNumber,
                          account?.currency,
                          index
                        )
                      }
                    >
                      <div style={{ flex: 1 }}>
                        <Typography.Text>
                          {account.accountNumber}{' '}
                          <span className="currency">{account.currency}</span>
                        </Typography.Text>
                      </div>
                      {recipientAccountId === account.accountId && <Checkmark />}
                    </AccountRow>
                  ))}
                </div>
              </Panel>
            </StyledCollapse>
          )
        )}
        <FullWidthButton onClick={onBack}>{t('back')}</FullWidthButton>
        <PrimaryButton
          type="primary"
          onClick={() => recipientAccountId && onNext(recipientAccountId)}
          disabled={!hasAccounts || !recipientAccountId}
        >
          {recipientAccountId ? t('continue') : t('selectAccount')}
        </PrimaryButton>
      </ColumnContainer>
    </div>
  );
};

export default SelectAccounts;
